import { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { IMainMenuViewOption } from '@rbi-ctg/menu';
import { useFeatureMenu } from 'hooks/use-feature-menu';
import { excludeDisabledMenuSections, separateBreakfastMenu } from 'pages/main-menu/utils';
import { useDayPartContext } from 'state/day-part';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { DAY_PART_SELECTIONS } from 'state/main-menu/types';
import { useMenuContext } from 'state/menu';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { optionToUrl } from 'utils/menu';
import { useIsBrowseMenu } from 'utils/menu/hooks/use-is-browse-menu';
import {
  isFavoritesRoute,
  isItemPickerOrComboRoute,
  isRecentItemsRoute,
  relativeRoutes,
  routes,
} from 'utils/routing';

import { IGetTabbedMenuData, IuseTabbedHeaderProps } from './types';

function concatValidItems<T>(dataToConcat: (T | null)[], data: T[]): T[] {
  const items = dataToConcat.filter((item): item is T => {
    return item !== null;
  });

  return items.concat(data);
}

function getTabbedMenuData({
  dayPartMenu,
  mainMenuItems,
  activeDayParts = [],
  showBurgersForBreakfast,
  serviceMode,
  showStaticMenu,
}: IGetTabbedMenuData) {
  const activeMenuOptions = activeDayParts?.length
    ? excludeDisabledMenuSections({
        menuData: mainMenuItems,
        activeDayParts,
        showBurgersForBreakfast,
        serviceMode,
        showStaticMenu,
      }) || []
    : mainMenuItems;

  if (dayPartMenu === DAY_PART_SELECTIONS.ALL_KEY || !dayPartMenu) {
    return activeMenuOptions;
  }

  const { menuData, breakfastMenuData } = separateBreakfastMenu(activeMenuOptions);

  const shouldRenderBreakfast =
    !!breakfastMenuData?.length && dayPartMenu === DAY_PART_SELECTIONS.BREAKFAST_KEY;
  return shouldRenderBreakfast ? breakfastMenuData : menuData;
}

export default function useTabbedMenuHeader({
  dayPartMenu,
  activeDayParts,
}: IuseTabbedHeaderProps) {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const { isBrowseMenu } = useIsBrowseMenu(location.pathname);
  const navigate = useNavigate();
  const enableTabbedMenuBarRecents = useFlag(LaunchDarklyFlag.ENABLE_TABBED_MENU_BAR_RECENTS);
  const enableTabbedMenuBarFavorites = useFlag(LaunchDarklyFlag.ENABLE_TABBED_MENU_BAR_FAVORITES);
  const enableMenuServiceData = useFlag(LaunchDarklyFlag.ENABLE_MENU_SERVICE_DATA);
  const { serviceMode } = useServiceModeContext();
  const { activeDayParts: currentActiveDayParts } = useDayPartContext();
  const {
    getMainMenu,
    getPricingAndAvailability,
    getDayPart,
    showStaticMenu,
    showBurgersForBreakfast,
    data,
    loading,
    isStaticMenu,
  } = useMenuContext();

  const { region } = useLocale();
  const { store } = useStoreContext();
  const { menuId } = useFeatureMenu();

  const mainMenu = useMemo(
    () => getMainMenu(menuId, region, store?.number),
    [getMainMenu, menuId, region, store]
  );

  const mainMenuBaseItems: IMainMenuViewOption[] =
    ((enableMenuServiceData
      ? data?.Menu?.options?.filter(item => {
          if (isStaticMenu) {
            return true;
          }
          if (item && item.__typename === 'Combo') {
            return false;
          }

          // @ts-ignore
          return getPricingAndAvailability(item?._id)?.isAvailable;
        })
      : mainMenu.data?.options) as IMainMenuViewOption[]) || [];

  const { currentDayPart } = getDayPart({ showStaticMenu });

  const menuItems = getTabbedMenuData({
    mainMenuItems: mainMenuBaseItems,
    dayPartMenu: dayPartMenu ?? currentDayPart,
    activeDayParts: activeDayParts ?? currentActiveDayParts,
    showBurgersForBreakfast,
    serviceMode,
    showStaticMenu,
  });

  const tabbedMenuData = menuItems.map(item => ({
    tabId: item._id,
    label: item.name?.locale,
    to: optionToUrl(item, isBrowseMenu),
  }));

  const fullMenuLabelKey = 'fullMenu';
  const recentsLabelKey = 'recents';
  const favoriteLabelKey = 'favorite';
  const fullMenu = {
    tabId: fullMenuLabelKey,
    label: formatMessage({ id: fullMenuLabelKey }),
    to: routes.menu,
  };
  const recentItems = enableTabbedMenuBarRecents
    ? {
        tabId: recentsLabelKey,
        label: formatMessage({ id: recentsLabelKey }),
        to: `${routes.menu}/${relativeRoutes.recentItems}`,
      }
    : null;

  const favorites = enableTabbedMenuBarFavorites
    ? {
        tabId: favoriteLabelKey,
        label: formatMessage({ id: favoriteLabelKey }),
        to: routes.favorites,
      }
    : null;

  // check if the current path is recentItems or favorites.
  // If it is and its disabled then redirect to the root menu
  if (
    (!recentItems && isRecentItemsRoute(location.pathname)) ||
    (!favorites && isFavoritesRoute(location.pathname))
  ) {
    navigate(routes.menu);
  }

  const baseItems = [fullMenu, recentItems, favorites];
  const tabbedHeaderItems = concatValidItems(baseItems, tabbedMenuData);

  const activeTab = tabbedHeaderItems.findIndex(item => item.to === location.pathname);
  const isRouteOnItemPickerAndOutsideTabbedItems =
    isItemPickerOrComboRoute(location.pathname) && activeTab === -1;

  return {
    activeTab,
    isRouteOnItemPickerAndOutsideTabbedItems,
    isTabbedMenuLoading: enableMenuServiceData
      ? !data || loading
      : !mainMenu.data || mainMenu.loading,
    tabbedHeaderItems,
  };
}
